// ReportApp — "the app is broken", from inside the app.
//
// NOT the same thing as /problem. That one is "this JOB is blocked" and goes
// to the office through the offline queue; this is "this SCREEN is wrong" and
// becomes a GitHub issue (server/routes/feedback.js). A crew has both problems
// and they go to different people.
//
// It nearly did not survive the rebuild. The classic control lived in the
// desktop sidebar's version bar — inside an `<aside>` a phone pushes off
// screen with translateX(-100%) — so for months it was reported as "don't see
// the feedback/report button anywhere", which was accurate. It is on Me here,
// which every role reaches at every width from the same tab.
//
// Deliberately low-friction: crews are outdoors, often one-handed, often on a
// bad connection. Two taps and a sentence. Anything more and nobody reports
// anything, which is worse than an imperfect report.
//
//   POST /feedback  { type, message, page, diagnostics, photos? }
//                   → { number, duplicate } | { saved } | 400 { error }
//
// ── Pictures, plural ────────────────────────────────────────────────────────
//
// The owner asked for it in one sentence: "allow uploading pictures when
// reporting problems". A crew member standing in a yard has more than one
// thing to show — the screen that is wrong, what they were actually looking
// at, the same screen after they tried again — and one image was the reason
// half of them attached nothing at all.
//
// The rules are in reportPhotos.js, mirrored from the server and checked
// against it, and every one of them is applied HERE rather than left to the
// server: a picture refused after a slow upload on a field connection is a
// picture the person has already waited for.
//
// The rule that outranks all of them: A PICTURE MUST NEVER COST THE WORDS.
// One photo failing does not stop the others attaching, no photo failing stops
// the report sending, and Send is never disabled by anything to do with an
// image.

import React, { useRef, useState } from 'react';
import { Sheet, FwButton, FwField, FwTextarea, FwSelect, Notice, SectionLabel } from '../../ui/fw';
import { post, describeError, isOffline } from './http';
import { collectDiagnostics } from '../../utils/diagnostics';
import { downscaleImage } from '../../utils/screenshot';
import { MAX_PHOTOS, refusePhoto, describeBytes } from './reportPhotos';
import './ReportApp.css';

export const TYPES = [
  { value: 'bug', label: 'Something is wrong' },
  { value: 'idea', label: 'An idea' },
];

// ── The three-tap report (issue #54) ────────────────────────────────────────
//
// Two questions before any typing, because a report nobody can triage without
// reading free text is a report that waits for somebody to read it.
//
// NEITHER of them asks how urgent it is, and that is the whole design. The
// issue: "under stress everything is critical. A self-declared `critical` flag
// inflates until it means nothing, and then the one that matters gets ignored
// with the rest." So the first question asks about a FACT the person can
// answer while standing in a yard — can you record your time? — and the server
// corroborates it against the database before anything is labelled
// (server/utils/feedbackTriage.js). The tap is one input to the label. It is
// never the only one.
//
// Q1 is the payroll axis and the only one that can be an emergency, because
// hours are the one thing in this app that cannot be reconstructed later. Q2
// is about the day, not the pay, and can wait until tonight however annoying
// it is.
//
// The keys and the values are a CONTRACT with feedbackTriage.js, which drops
// anything it does not recognise — so a rename here would send a report that
// triages as if nobody had been asked, silently. ReportApp.test.js reads the
// server module and compares.
export const QUESTIONS = [
  {
    key: 'canRecordTime',
    label: 'Can you still record your time?',
    // No "not sure" on purpose. This answer decides whether payroll data is
    // being lost; a maybe would be read either as a No, inflating the one
    // signal that is supposed to mean something, or as a Yes, losing it.
    options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }],
  },
  {
    key: 'canFinishJob',
    label: 'Can you finish today’s job?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'unsure', label: 'Not sure' },
    ],
  },
];

/**
 * What a worker is told the instant they answer No to Q1 — before anything is
 * submitted, and before any fix exists anywhere.
 *
 * This is the most useful thing on the screen and it is not code. The issue:
 * "the fastest safe fix is often NOT a deploy — deploying at 10am stops PM2
 * and takes the app down for everyone, including the crews who are working
 * fine." Writing the times on paper removes the data loss in thirty seconds
 * without touching production. An emergency is a reason to act immediately; it
 * is not automatically a reason to deploy immediately.
 *
 * The server repeats these exact words into the issue, so whoever picks it up
 * knows the hours are already safe. Both sides are pinned to one another by a
 * test.
 */
export const MITIGATION = 'Write your start and end times down. Your manager '
  + 'can enter them. Your pay will be correct.';

const EMPTY_ANSWERS = { canRecordTime: null, canFinishJob: null };

/** Did an answer actually report something wrong? */
function saysSomethingIsWrong(answers) {
  return answers.canRecordTime === 'no'
    || answers.canFinishJob === 'no'
    || answers.canFinishJob === 'unsure';
}

/**
 * Send needs either words or a problem.
 *
 * "Yes, yes, and nothing typed" is not a report — it is somebody who opened
 * the sheet and answered two questions — and filing it would spend a slot in
 * a tracker on nothing. Everything else is a report: three taps with no
 * keyboard is the whole point of the design, for a person outdoors in gloves
 * whose app has just failed them.
 */
export function canSend(message, answers = EMPTY_ANSWERS) {
  return String(message || '').trim().length > 0 || saysSomethingIsWrong(answers);
}

/**
 * The words to send when they typed none.
 *
 * Their own sentence always wins — nothing here ever edits or appends to it.
 * Only when the box is empty does the app say in words what the taps said,
 * because the server's five-character rule is the authority and because
 * buildTitle() makes the issue's first line out of this.
 */
export function composeMessage(message, answers = EMPTY_ANSWERS) {
  const typed = String(message || '').trim();
  if (typed) return typed;

  const said = [];
  if (answers.canRecordTime === 'no') said.push('I cannot record my time.');
  else if (answers.canRecordTime === 'yes') said.push('I can still record my time.');
  if (answers.canFinishJob === 'no') said.push('I cannot finish today’s job.');
  else if (answers.canFinishJob === 'unsure') said.push('I am not sure I can finish today’s job.');
  if (!said.length) return '';

  said.push('(Sent from the buttons — nothing was typed.)');
  return said.join(' ');
}

/** The one client-side rule. The server's is the same, and it is the authority. */
export function reportProblemWith(message) {
  return String(message || '').trim().length < 5
    ? 'Please add a little more detail so we can find it.'
    : null;
}

/**
 * @param {boolean}  open
 * @param {Function} onClose
 * @param {string}   [prefill]  the box's INITIAL text. Read once, as initial
 *   state — never as an effect. This sheet is mounted permanently by Me and
 *   has no reset-on-open (its `sessionRef` below records why), so a caller
 *   seeding it does so by changing the component's `key` and remounting. An
 *   effect writing into `message` would race a report already being typed.
 */
export default function ReportApp({ open, onClose, prefill = '' }) {
  const [type, setType] = useState('bug');
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [message, setMessage] = useState(prefill || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [shotBusy, setShotBusy] = useState(false);
  const fileRef = useRef(null);

  /**
   * A stable identity for each attached picture.
   *
   * It is what Remove names, and it cannot be the data URL: the same photo can
   * legitimately be attached twice — picked once from the roll and once from
   * the camera, or just picked twice by somebody outdoors who could not tell
   * on a 2-inch thumbnail — and removing "the one that looks like this" would
   * take both. Nor the index, which every remove renumbers.
   *
   * (Measured, rule 8: keying the LIST by index instead of this is not
   * observable — React updates `src` on the reused node either way — so that
   * is not the reason, and this comment used to claim it was.)
   */
  const photoSeq = useRef(0);

  /**
   * Which dialog session a pending await belongs to.
   *
   * The sheet is mounted permanently by Me, so dismissing it does not unmount
   * this component and does not cancel anything already in the air. Without a
   * session check, a send or a downscale that finishes AFTER the dismissal
   * writes into a form that has been reset — and the next person to open
   * "Report a problem" is shown a Thank-you, or a red error, belonging to a
   * report they walked away from. That is the same shape as every stale-answer
   * bug in docs/AGENT_LEARNINGS.md: state that survives the request it
   * describes makes a claim about something nobody asked.
   */
  const sessionRef = useRef(0);

  /**
   * The questions are about a BUG, not about an idea.
   *
   * "Can you still record your time?" is a nonsense question to ask somebody
   * suggesting the map could open bigger — and answering it would put a
   * `blocking` label on an enhancement, which is exactly the inflation the
   * whole design is there to prevent. So they are hidden for an idea, and the
   * answers go with them rather than being sent unseen.
   */
  const asked = type === 'bug';
  const pickType = (value) => {
    setType(value);
    if (value !== 'bug') setAnswers(EMPTY_ANSWERS);
  };
  const answer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const close = () => {
    sessionRef.current += 1;
    // The answers are reset with everything else, and this one matters more
    // than the draft does: Me mounts this sheet permanently, so without it the
    // next person to open Report a problem inherits somebody else's "I cannot
    // record my time" — which is not a stale draft, it is a false emergency.
    setType('bug'); setAnswers(EMPTY_ANSWERS); setMessage(''); setError(null); setSent(null);
    setBusy(false); setPhotos([]); setShotBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    if (onClose) onClose();
  };

  /**
   * Attach one or more pictures.
   *
   * Downscaling happens on the DEVICE. A phone photo is 3–8 MB, which the
   * server refuses and a field connection struggles with.
   *
   * ── One picture failing must not take the others ────────────────────────
   *
   * Somebody who selects four photos at once and has one unreadable file among
   * them keeps the other three. Everything is decided against the list as it
   * grows — the count, the per-picture size, the total — because the person
   * picked them in one gesture and the answer has to be about the set.
   *
   * Failures are collected and shown as ONE sentence at the end rather than
   * replacing each other, so the last file picked does not decide which
   * problem gets reported.
   */
  const pickPhotos = async (e) => {
    const files = Array.from((e.target && e.target.files) || []);
    // Cleared immediately so picking the SAME file twice still fires a change
    // event — otherwise somebody who removes a picture cannot re-attach it.
    e.target.value = '';
    if (!files.length) return;
    const session = sessionRef.current;
    setShotBusy(true);
    setError(null);

    const added = [];
    const problems = [];
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop -- deliberately serial: a
        // phone decoding four full-size photos at once is how a mid-range
        // Android runs out of memory mid-report.
        const shrunk = await downscaleImage(file);
        if (sessionRef.current !== session) return;
        const refusal = refusePhoto(shrunk, [...photos, ...added]);
        if (refusal) { problems.push(refusal); continue; }
        photoSeq.current += 1;
        added.push({ ...shrunk, id: `p${photoSeq.current}` });
      } catch (err) {
        if (sessionRef.current !== session) return;
        problems.push(err.message || 'That image could not be used.');
      }
    }

    if (sessionRef.current !== session) return;
    if (added.length) setPhotos((prev) => [...prev, ...added]);
    // Deduplicated: picking six photos at the limit would otherwise print the
    // same sentence four times.
    if (problems.length) setError([...new Set(problems)].join(' '));
    setShotBusy(false);
  };

  const removePhoto = (id) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
    setError(null);
  };

  const send = async () => {
    // What actually goes: their sentence if they typed one, otherwise the taps
    // put into words. The five-character rule is applied to whichever it is,
    // so the server's own validator can never be the thing that refuses a
    // report the app just told somebody it would send.
    const body = composeMessage(message, asked ? answers : EMPTY_ANSWERS);
    const problem = reportProblemWith(body);
    if (problem) { setError(problem); return; }
    const session = sessionRef.current;
    setBusy(true);
    setError(null);
    try {
      const data = await post('/feedback', {
        type,
        message: body,
        // Omitted entirely when neither question was answered, so the server
        // can tell "did not answer" from a set of nulls — and so a phone still
        // running last week's bundle, which sends nothing, looks the same.
        ...(asked && (answers.canRecordTime || answers.canFinishJob) ? { answers } : {}),
        // Which screen they were on matters more than anything else they can
        // tell us, and they should not have to describe it.
        page: window.location.pathname,
        // Collected HERE rather than on open, so the measurements describe the
        // page as it is at the moment they press Send.
        diagnostics: collectDiagnostics(),
        // Omitted entirely when there are none, so the server can tell "did
        // not attach" from "attached something unusable".
        ...(photos.length ? { photos: photos.map((p) => p.dataUrl) } : {}),
      });
      if (sessionRef.current !== session) return;
      setSent(data || {});
    } catch (err) {
      if (sessionRef.current !== session) return;
      // describeError's offline branch no longer promises anything — it said
      // "No connection. Your work is saved on this phone." on every
      // NetworkError until 2026-09-06, and this catch was written to escape it
      // (#575 §1 fixed it at the source, for every screen). It now returns
      // "No connection. Could not send that. Please try again.", which is
      // true.
      //
      // This branch stays because it says one more thing that generic sentence
      // cannot: the text is still IN THE BOX. Nothing below clears `message`,
      // so Send is one tap away once there are bars — and this dialog exists
      // because crews are on bad connections, so the offline case is the
      // expected one, not the edge.
      setError(isOffline(err)
        ? 'No signal, so this was not sent. It is still in the box — try again when you have one.'
        : describeError(err, 'Could not send that. Please try again.'));
    } finally {
      if (sessionRef.current === session) setBusy(false);
    }
  };

  return (
    <Sheet open={open} title={sent ? 'Thank you' : 'Report a problem with the app'} onClose={close}>
      {sent ? (
        <>
          {/* Three answers, because three things can actually have happened and
              telling them apart is the honesty rule this dialog is built on:
              filed in the tracker, added to a report that already existed, or —
              when the tracker could not be reached — saved and sent straight to
              a manager. The last one deliberately promises less: there is no
              reference number, because nothing was filed. */}
          <Notice
            tone="ok"
            title={sent.saved ? 'Saved and sent to your manager'
              : sent.duplicate ? 'Added to an existing report' : 'Sent'}
          >
            {sent.saved
              ? 'The usual place could not be reached, so this went straight to your manager instead. It is saved — you do not need to send it again.'
              : sent.duplicate
                ? 'Somebody has already reported this, so yours was added to it. That is useful — it says how often it happens.'
                : 'It is filed. You do not need to do anything else.'}
            {sent.number ? ` Reference #${sent.number}.` : ''}
          </Notice>
          <FwButton variant="ghost" size="lg" full onClick={close}>Close</FwButton>
        </>
      ) : (
        <>
          {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
          <FwField label="What kind of thing" htmlFor="fw-report-type">
            <FwSelect id="fw-report-type" value={type} onChange={(e) => pickType(e.target.value)}>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </FwSelect>
          </FwField>
          {asked ? (
            <div className="rp-ask">
              {QUESTIONS.map((q) => (
                <div className="rp-q" key={q.key}>
                  <SectionLabel>{q.label}</SectionLabel>
                  {/* A group of toggles, the same shape as the Field /problem
                      screen's severity row and for the same reason: the
                      RESTING state is a plain outline and the CHOSEN one is
                      solid, with a tick in it and aria-pressed="true". Three
                      signals — weight, a glyph, and the accessible state — so
                      it survives sunlight and a red-green crew member, neither
                      of which a colour alone does.

                      Both chosen states are `primary`. A red No would be the
                      screen grading its own answer, and spending the danger
                      colour on a factual question is how the danger colour
                      stops meaning anything. What the No actually means is
                      said in words, underneath, where it can be acted on. */}
                  <div className="rp-answers" role="group" aria-label={q.label}>
                    {q.options.map((o) => {
                      const picked = answers[q.key] === o.value;
                      return (
                        <FwButton
                          key={o.value}
                          size="lg"
                          variant={picked ? 'primary' : 'ghost'}
                          icon={picked ? 'check' : undefined}
                          aria-pressed={picked ? 'true' : 'false'}
                          data-answer={`${q.key}:${o.value}`}
                          onClick={() => answer(q.key, o.value)}
                        >
                          {o.label}
                        </FwButton>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {asked && answers.canRecordTime === 'no' ? (
            /* Shown the instant they tap it: before Send, before an issue
               exists, before anybody has looked at anything. It is the whole
               point of asking — the hours stop being lost now, on paper, and
               whether a fix deploys today or tonight becomes a separate and
               much calmer question. */
            <Notice tone="warn" title="Write your times down — your pay will still be right">
              {MITIGATION}
            </Notice>
          ) : null}
          <FwField
            label="What happened"
            htmlFor="fw-report-text"
            hint={asked && saysSomethingIsWrong(answers)
              ? 'Optional now — the answers above are enough to act on. One sentence still helps.'
              : 'One sentence is plenty. We can already see which screen you are on.'}
            required={!(asked && saysSomethingIsWrong(answers))}
          >
            <FwTextarea
              id="fw-report-text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="The Clock in button does nothing on the second job"
            />
          </FwField>
          <FwField
            label="Pictures, if they help"
            htmlFor="fw-report-photos"
            hint={photos.length
              ? `${photos.length} of ${MAX_PHOTOS} attached.`
              : `Optional. Up to ${MAX_PHOTOS} — a photo of the screen, or of the thing itself.`}
          >
            {/* `multiple` and `accept="image/*"`: on a phone this offers the
                camera and the camera roll, and the roll lets several be picked
                in one go. The control is never disabled at the limit — a dead
                control explains nothing, and refusePhoto() answers with a
                sentence that says what to do instead. */}
            <input
              id="fw-report-photos"
              className="rp-file"
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={pickPhotos}
            />
          </FwField>
          {photos.length ? (
            <ul className="rp-shots" data-testid="rp-shots">
              {photos.map((p, i) => (
                <li className="rp-shot" key={p.id}>
                  {/* The picture is shown back because a photo taken one-handed
                      in a yard is often of the wrong thing, and a filename does
                      not tell anybody that. */}
                  {/* The alt says only which one it is: a screen reader already
                      announces an <img> as an image, and jsx-a11y fails the
                      build for repeating it. */}
                  <img className="rp-shot__img" src={p.dataUrl} alt={`Attachment ${i + 1}`} />
                  <span className="rp-shot__size">{describeBytes(p.bytes)}</span>
                  <FwButton
                    variant="ghost"
                    full
                    data-photo-remove={p.id}
                    onClick={() => removePhoto(p.id)}
                  >
                    Remove
                  </FwButton>
                </li>
              ))}
            </ul>
          ) : null}
          {/* A picture that FAILED never disables this — a report with no image
              is worth far more than no report. `shotBusy` is the other thing:
              while a picture is still being decoded, sending would quietly file
              the report without the photo the person believes they attached.
              That lasts a second or two, not until they fix anything. */}
          <FwButton
            size="lg"
            full
            onClick={send}
            loading={busy || shotBusy}
            disabled={!canSend(message, asked ? answers : EMPTY_ANSWERS)}
          >
            Send
          </FwButton>
        </>
      )}
    </Sheet>
  );
}
